import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import produce from 'immer';
import { message } from 'antd';
import { csReviewActions } from '../../../../../store/modules/cs/review';
import { appActions } from '../../../../../store/modules/app';
import { useTagListQuery } from './queries/useTagListQuery';
import { useQueryClient } from '@tanstack/react-query';
import { useRelatedTagListQuery } from './queries/useRelatedTagListQuery';
import useTagExcelUpload from './queries/useTagExcelUpload';
import { useProductTagListQuery } from './queries/useProudctTagListQuery';
import { ProductRelatedTagsDto, TagsFindResponseBodyDto, TagsFindResponseBodyTagsDto } from '../types/TagListTypes';
import { TAG_TABLE_INITIAL_OPTION } from '../constants';
import { NO_DATA_ON_EXCEL_DOWNLOAD_WARNING_TEXT } from '../../../../../constants/service/common/utilConstants';
import { convertTagsDtoToFindResponseDto } from '../util/util';
import { TAG } from '../../../../../constants/queryKeys';
import { TAG_STATUS_V2_TYPE } from '../../../../../constants/service/app/tagConstants';

interface TableOptions {
  sort: { id: string };
  id: number | null;
  name: string | null;
  page: number;
  pageSize: number;
}

interface Options {
  filterOption: {
    name: string;
    id: string;
  };
  sortOption: {
    id: 'descend' | 'ascend';
  };
  pagination: {
    pageNum: number;
    pageSize: number;
  };
}

type UseTagListReturnType = {
  onChangeSelectedTagList: (action: string, moveIdList: number[]) => void;
  onClickAllTagListExcelDownload: (e: React.MouseEvent) => void;
  onClickSelectedTagListExcelDownload: (e: React.MouseEvent) => void;
  onClickRelatedTagList: (id: string) => void;
  onChangeTableOptions: (options: Options) => void;
  onClickTagExcelUpload: (file: File) => void;
  onClickProductTagList: (id: number) => void;
  tagList: TagsFindResponseBodyDto | undefined;
  tableOptions: TableOptions;
  selectedTagList: TagsFindResponseBodyTagsDto[];
  setSelectedTagList: (tags: TagsFindResponseBodyTagsDto[]) => void;
};

const useTagList = (): UseTagListReturnType => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { setValue } = useFormContext();
  const [tableOptions, setTableOptions] = useState<TableOptions>(TAG_TABLE_INITIAL_OPTION);
  const [selectedTagList, setSelectedTagList] = useState<TagsFindResponseBodyTagsDto[]>([]);
  const [tagId, setTagId] = useState<string>('');
  const [productId, setProductId] = useState<number | null>(null);

  const { data: tagList } = useTagListQuery<TagsFindResponseBodyDto>({
    payload: {
      ...tableOptions,
      page: tableOptions.page + 1,
      sort: tableOptions.sort.id,
      status: TAG_STATUS_V2_TYPE.ACTIVE as 'active',
    },
    options: {
      keepPreviousData: true,
      suspense: true,
    },
  });

  useRelatedTagListQuery({
    id: tagId,
    options: {
      meta: {
        successCallback: (data) => {
          const { tags } = data;
          dispatch(
            appActions.appToggleModal({
              name: 'tagListViewModal',
              data: {
                tagList: tags,
              },
            }),
          );
          setTagId('');
        },
      },
    },
  });

  const { data: productTagList, isSuccess: productTagGetSuccess } = useProductTagListQuery({
    payload: {
      productId,
    },
    options: {
      select: (data) => data.data.productTagList,
    },
  });

  const { mutate: uploadExcelFile, isSuccess: uploadSuccess, data: uploadData } = useTagExcelUpload();

  const handleChangeSelectedTagList = (action: string, moveIdList: number[]) => {
    setSelectedTagList(
      produce(selectedTagList, (draft) => {
        if (action === 'add') {
          const newSelectedTags = tagList?.tags.filter(
            (item) => moveIdList.includes(item.id) && !draft.some((selectedItem) => selectedItem.id === item.id),
          );
          if (newSelectedTags) draft.push(...newSelectedTags);
        } else if (action === 'remove') {
          return draft.filter((item: TagsFindResponseBodyTagsDto) => !moveIdList.includes(item.id));
        }
      }),
    );
  };

  const handleClickAllTagListExcelDownload = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(
      csReviewActions.getReviewDetailTagAllListExcelDownload(
        {
          tagId: tableOptions.id,
          tagName: tableOptions.name,
        },
        {
          id: tableOptions.sort.id.includes('-') ? 'descend' : 'ascend',
        },
      ),
    );
  };

  const handleSelectedTagExcelDownload = (e: React.MouseEvent) => {
    e.preventDefault();

    if (!selectedTagList.length) {
      message.error(NO_DATA_ON_EXCEL_DOWNLOAD_WARNING_TEXT);
      return;
    }
    dispatch(
      csReviewActions.getReviewDetailTagSelectedListExcelDownload({
        tagIdList: selectedTagList.map((item) => item.id),
      }),
    );
  };

  const handleClickRelatedTagList = (id: string) => {
    setTagId(id);
  };

  const handleChangeTableOptions = (options: Options) => {
    setTableOptions((prev) =>
      produce(prev, (draft) => {
        Object.assign(draft, options.filterOption);
        draft.page = options.pagination.pageNum;
        draft.pageSize = options.pagination.pageSize;
        draft.sort.id = options.sortOption.id === 'descend' ? '-id' : 'id';
      }),
    );
  };

  const handleTagExcelUpload = (file: File) => {
    uploadExcelFile(file);
  };

  const handleUpdateProductTagList = (id: number) => {
    setProductId(id);
  };

  const getUpdatedTagsWithSelection = (
    tags: TagsFindResponseBodyTagsDto[],
    selectedIds: number[],
  ): TagsFindResponseBodyTagsDto[] => {
    return tags.map((tag) => {
      const uncheckable = selectedIds.includes(tag.id);
      return {
        ...tag,
        uncheckable,
      };
    });
  };

  const setQueryDataWithUpdatedTags = (updatedTags: TagsFindResponseBodyTagsDto[]): void => {
    queryClient.setQueryData<TagsFindResponseBodyDto>(
      [
        ...TAG.LIST,
        {
          ...tableOptions,
          page: tableOptions.page + 1,
          sort: tableOptions.sort.id,
        },
      ],
      () => ({
        ...tagList,
        tags: updatedTags,
        totalCount: tagList?.totalCount ?? 0,
      }),
    );
  };

  useEffect(() => {
    if (tagList?.tags) {
      const selectedIds = selectedTagList?.map((item) => item.id);
      const updatedTags = getUpdatedTagsWithSelection(tagList.tags, selectedIds);
      setQueryDataWithUpdatedTags(updatedTags);
    }
  }, [tagList, selectedTagList, tableOptions, queryClient]);

  useEffect(() => {
    if (uploadSuccess && uploadData?.data?.itemList) {
      const selectedTags = convertTagsDtoToFindResponseDto(uploadData?.data?.itemList);
      setSelectedTagList(selectedTags);
      setValue('tagIds', selectedTags);
    }
  }, [uploadSuccess, uploadData, setValue]);

  useEffect(() => {
    if (productTagGetSuccess && productTagList) {
      const selectedTags = convertTagsDtoToFindResponseDto(productTagList as ProductRelatedTagsDto[]);
      setSelectedTagList(selectedTags);
      setValue('tagIds', selectedTags);
    }
  }, [productTagGetSuccess, productTagList, setValue]);

  return {
    onChangeSelectedTagList: handleChangeSelectedTagList,
    onClickAllTagListExcelDownload: handleClickAllTagListExcelDownload,
    onClickSelectedTagListExcelDownload: handleSelectedTagExcelDownload,
    onClickRelatedTagList: handleClickRelatedTagList,
    onChangeTableOptions: handleChangeTableOptions,
    onClickTagExcelUpload: handleTagExcelUpload,
    onClickProductTagList: handleUpdateProductTagList,
    tagList,
    tableOptions,
    selectedTagList,
    setSelectedTagList,
  };
};

export default useTagList;
